<template>
  <div class="dev-docs">
    <h2 class="dev-docs__title">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
      Integration Guide
    </h2>
    <p class="dev-docs__desc">
      Add live cricket widgets to your website in minutes.
      Follow the steps below to get started.
    </p>

    <div class="dev-docs__steps">
      <h4>Getting Started</h4>
      <ol>
        <li class="dev-docs__step-item">
          <span class="dev-docs__step-text">
            <strong>Get your Public Key</strong>
            &mdash; Your Public Key is shown in the
            <strong>Credentials</strong> box on the right.
            If you haven't created one yet, click
            <strong>"Generate Public Key"</strong> above.
            This key identifies your account and
            authorizes the widget to load on your site.
          </span>
        </li>
        <li
          class="dev-docs__step-item"
          :class="{ 'dev-docs__step-item--disabled': !publishableKey }"
        >
          <span class="dev-docs__step-text">
            <strong>Whitelist your website</strong>
            &mdash; In the <strong>Configure</strong> panel
            above, add the URL of the site where the widget
            will appear (e.g.
            <code>https://yoursite.com</code>). This ensures
            the widget only loads on domains you approve.
            You can add up to 5 URLs.
          </span>
          <span class="dev-docs__step-note">
            <strong>Testing locally?</strong> Add your local
            dev URL too (e.g.
            <code>http://localhost:3000</code>).
          </span>
        </li>
        <li class="dev-docs__step-item">
          <span class="dev-docs__step-text">
            <strong>Install the widget package</strong>
            &mdash; In your project terminal, run:
          </span>
          <button
            class="dev-docs__step-copy"
            @click="copyCmd('npm install @roanuz/ants-widgets-sdk', 'install')"
          >
            <code class="dev-docs__step-cmd">
              npm install @roanuz/ants-widgets-sdk
            </code>
            <span class="dev-docs__step-copy-label">
              {{ cmdCopied === 'install' ? 'Copied!' : 'Copy' }}
            </span>
          </button>
        </li>
        <li class="dev-docs__step-item">
          <span class="dev-docs__step-text">
            <strong>Copy the widget file to your site</strong>
            &mdash; Move the widget script into your
            project's public folder so it can be served
            to visitors:
          </span>
          <button
            class="dev-docs__step-copy"
            @click="copyCmd('cp node_modules/@roanuz/ants-widgets-sdk/dist/ants-widgets.js public/', 'copy')"
          >
            <code class="dev-docs__step-cmd">
              cp node_modules/@roanuz/ants-widgets-sdk/dist/ants-widgets.js public/
            </code>
            <span class="dev-docs__step-copy-label">
              {{ cmdCopied === 'copy' ? 'Copied!' : 'Copy' }}
            </span>
          </button>
          <span class="dev-docs__step-note">
            This is a single self-contained file &mdash;
            styles are bundled in, no separate stylesheet needed.
          </span>
        </li>
        <li class="dev-docs__step-item">
          <span class="dev-docs__step-text">
            <strong>Add the widget to your page</strong>
            &mdash; Pick your framework below, copy the
            ready-to-use code snippet, and paste it into
            your project. Your Public Key is pre-filled.
          </span>
        </li>
      </ol>
    </div>

    <div class="dev-docs__widget-selector">
      <h4>Choose Widget</h4>
      <div class="dev-docs__widget-tabs">
        <button
          class="dev-docs__widget-tab"
          :class="{
            'dev-docs__widget-tab--active':
              activeWidgetType === 'matches'
          }"
          @click="activeWidgetType = 'matches'"
        >
          Matches Widget
        </button>
        <button
          class="dev-docs__widget-tab"
          :class="{
            'dev-docs__widget-tab--active':
              activeWidgetType === 'tournament'
          }"
          @click="activeWidgetType = 'tournament'"
        >
          Tournament Widget
        </button>
      </div>
      <p class="dev-docs__widget-desc">
        {{ widgetDescriptions[activeWidgetType] }}
      </p>

      <div class="dev-docs__code-preview">
        <div class="dev-docs__code-side">
          <div class="dev-docs__tabs">
            <button
              v-for="tab in docTabs"
              :key="tab.key"
              class="dev-docs__tab"
              :class="{
                'dev-docs__tab--active':
                  activeDocTab === tab.key
              }"
              @click="activeDocTab = tab.key"
            >{{ tab.label }}</button>
          </div>

          <div class="dev-docs__code-container">
            <div class="dev-docs__code-header">
              <span class="dev-docs__code-filename">
                {{ activeTabData.filename }}
              </span>
              <button
                class="dev-docs__copy-btn"
                @click="copySnippet()"
              >
                {{ snippetCopied ? 'Copied!' : 'Copy' }}
              </button>
            </div>
            <pre class="dev-docs__code"><code>{{ activeTabData.code }}</code></pre>
          </div>
        </div>

        <div class="dev-docs__preview-side">
          <div class="dev-docs__preview-label">
            Sample Implementation
          </div>
          <div class="dev-docs__preview-frame">
            <iframe
              :src="previewUrl"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="dev-docs__reference">
      <h4>Configuration Options</h4>
      <table class="dev-docs__table">
        <thead>
          <tr>
            <th>Option</th>
            <th>Type</th>
            <th>Required</th>
            <th>Default</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><code>widget</code></td>
            <td>'matches' | 'tournament'</td>
            <td>Yes</td>
            <td>&mdash;</td>
            <td>Which widget to render</td>
          </tr>
          <tr>
            <td><code>publishableKey</code></td>
            <td>string</td>
            <td>Yes</td>
            <td>&mdash;</td>
            <td>Your Public Key from the Credentials box</td>
          </tr>
          <tr>
            <td><code>baseUrl</code></td>
            <td>string</td>
            <td>Yes</td>
            <td>&mdash;</td>
            <td>API proxy URL</td>
          </tr>
          <tr>
            <td><code>wsUrl</code></td>
            <td>string</td>
            <td>No</td>
            <td>Same as baseUrl</td>
            <td>SSE endpoint for real-time updates</td>
          </tr>
          <tr>
            <td><code>tabs</code></td>
            <td>string[]</td>
            <td>No</td>
            <td>All tabs</td>
            <td>Tabs to show in match scorecard</td>
          </tr>
          <tr>
            <td><code>defaultTab</code></td>
            <td>string</td>
            <td>No</td>
            <td>'info'</td>
            <td>Default active tab</td>
          </tr>
        </tbody>
      </table>

      <h4>Available Tabs</h4>
      <table class="dev-docs__table">
        <thead>
          <tr>
            <th>Tab</th>
            <th>Description</th>
            <th>Live</th>
            <th>Completed</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="t in availableTabs" :key="t.tab">
            <td><code>{{ t.tab }}</code></td>
            <td>{{ t.desc }}</td>
            <td>{{ t.live }}</td>
            <td>{{ t.completed }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- <div class="dev-docs__links">
      <a
        href="https://ants-widget.sports.dev.roanuz.com/"
        target="_blank"
        rel="noopener"
        class="btn-style-common medium outlined"
      >View Sample Implementation</a>
    </div> -->
  </div>
</template>

<script>
import { getSnippet } from './widgetSnippets';

export default {
  name: 'WidgetIntegrationGuide',
  props: {
    publishableKey: { type: String, default: '' },
  },
  data() {
    return {
      activeDocTab: 'html',
      activeWidgetType: 'matches',
      snippetCopied: false,
      cmdCopied: null,
      widgetDescriptions: {
        matches: [
          'Shows featured match cards.',
          'Click any match to view the full scorecard',
          'with live updates, overs, partnerships,',
          'and more.',
        ].join(' '),
        tournament: [
          'Tournament browser with featured matches,',
          'tournament selection, and details',
          '(info, teams, stats, points table).',
        ].join(' '),
      },
      docTabs: [
        { key: 'html', label: 'HTML' },
        { key: 'vue', label: 'Vue.js' },
        { key: 'angular', label: 'Angular' },
        { key: 'react', label: 'React / Next.js' },
      ],
      availableTabs: [
        {
          tab: 'info',
          desc: 'Match information',
          live: 'Yes',
          completed: 'Yes',
        },
        {
          tab: 'scorecard',
          desc: 'Innings scorecard',
          live: 'Yes',
          completed: 'Yes',
        },
        {
          tab: 'live',
          desc: 'Ball-by-ball updates',
          live: 'Yes',
          completed: 'No',
        },
        {
          tab: 'overs',
          desc: 'Over-by-over breakdown',
          live: 'Yes',
          completed: 'Yes',
        },
        {
          tab: 'teams',
          desc: 'Squad / lineup',
          live: 'Yes',
          completed: 'Yes',
        },
        {
          tab: 'stats',
          desc: 'Partnerships',
          live: 'Yes',
          completed: 'Yes',
        },
        {
          tab: 'graph',
          desc: 'Performance charts',
          live: 'Yes',
          completed: 'Yes',
        },
        {
          tab: 'fantasy',
          desc: 'Fantasy statistics',
          live: 'Yes',
          completed: 'Yes',
        },
        {
          tab: 'odds',
          desc: 'Betting odds',
          live: 'Yes',
          completed: 'No',
        },
      ],
    };
  },
  computed: {
    activeTabData() {
      return getSnippet(
        this.activeDocTab,
        this.activeWidgetType,
        this.publishableKey,
      );
    },
    previewUrl() {
      const base = 'https://ants-widget.sports.dev.roanuz.com';
      return this.activeWidgetType === 'tournament'
        ? `${base}/preview/tournament`
        : `${base}/preview/matches`;
    },
  },
  methods: {
    copySnippet() {
      const { code } = this.activeTabData;
      navigator.clipboard.writeText(code).then(() => {
        this.snippetCopied = true;
        setTimeout(() => { this.snippetCopied = false; }, 2000);
      });
    },
    copyCmd(cmd, id) {
      navigator.clipboard.writeText(cmd).then(() => {
        this.cmdCopied = id;
        setTimeout(() => { this.cmdCopied = null; }, 2000);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.dev-docs {
  margin-top: size(10);

  &__title {
    @include font-styles($type: 'text', $size: 'lg', $weight: 'bold');
    display: flex;
    align-items: center;
    gap: size(10);
    margin-bottom: size(8);
    color: var(--global--main--title--Color);

    svg {
      width: size(28);
      height: size(28);
      padding: size(5);
      border-radius: 50%;
      background: var(--global--settings-bg--Color);
      stroke: var(--global--settings-img--Color);
    }
  }

  &__desc {
    @include font-styles($type: 'label', $size: 'lg', $weight: 'regular');
    color: var(--global--main--Color);
    margin-bottom: size(20);
    max-width: size(700);
  }

  &__steps {
    background: var(--global--white--Color);
    box-shadow: 0 size(1) size(3) rgba(0, 0, 0, 0.1),
      0 size(2) size(1) rgba(0, 0, 0, 0.06),
      0 size(1) size(1) rgba(0, 0, 0, 0.08);
    border-radius: size(8);
    padding: size(20);
    margin-bottom: size(20);

    h4 {
      @include font-styles($type: 'label', $size: 'lg', $weight: 'bold');
      color: var(--global--main--title--Color);
      margin-bottom: size(12);
    }

    ol {
      margin: 0;
      padding-left: size(28);
      counter-reset: step-counter;
      list-style: none;

      li {
        counter-increment: step-counter;
        position: relative;
        padding: size(8) 0;
        color: var(--global--main--Color);

        &::before {
          content: counter(step-counter);
          position: absolute;
          left: size(-28);
          top: size(10);
          width: size(20);
          height: size(20);
          border-radius: 50%;
          background: var(--global--settings-bg--Color);
          color: var(--global--settings-img--Color);
          font-size: size(11);
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    }
  }

  &__step-item {
    display: flex;
    flex-direction: column;
    gap: size(6);

    &--disabled {
      opacity: 0.45;
      pointer-events: none;
    }

    code {
      font-family: Menlo, Consolas, monospace;
      font-size: size(12);
      background: var(--global--primary--BackgroundColor);
      padding: size(1) size(4);
      border-radius: size(3);
      color: var(--global--common--Color);
    }
  }

  &__step-text {
    @include font-styles($type: 'label', $size: 'lg', $weight: 'regular');
    line-height: 1.5;
  }

  &__step-copy {
    display: inline-flex;
    align-items: center;
    gap: size(8);
    background: var(--global--primary--BackgroundColor);
    border: size(1) solid var(--global--border--Color);
    border-radius: size(4);
    padding: size(6) size(10);
    cursor: pointer;
    transition: background 0.15s;
    max-width: 100%;

    &:hover {
      background: var(--global--line--table--BackgroundColor);
    }
  }

  &__step-cmd {
    font-family: Menlo, Consolas, monospace;
    font-size: size(12);
    color: var(--global--common--Color);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__step-note {
    @include font-styles($type: 'label', $size: 'md', $weight: 'regular');
    color: var(--global--main--Color);
    background: var(--global--primary--BackgroundColor);
    border-left: size(3) solid var(--global--border--Color);
    padding: size(6) size(10);
    border-radius: size(3);
    line-height: 1.5;

    code {
      font-family: Menlo, Consolas, monospace;
      font-size: size(12);
      background: var(--global--line--table--BackgroundColor);
      padding: size(1) size(4);
      border-radius: size(3);
      color: var(--global--common--Color);
    }
  }

  &__step-code-block {
    font-family: Menlo, Consolas, monospace;
    font-size: size(12);
    background: var(--global--primary--BackgroundColor);
    border: size(1) solid var(--global--border--Color);
    border-radius: size(4);
    padding: size(10) size(12);
    color: var(--global--main--title--Color);
    line-height: 1.6;
    overflow-x: auto;

    code {
      font-family: inherit;
      font-size: inherit;
      background: none;
      padding: 0;
    }
  }

  &__step-copy-label {
    @include font-styles($type: 'label', $size: 'sm', $weight: 'bold');
    color: var(--global--common-link--Color);
    white-space: nowrap;
    padding-left: size(8);
    border-left: size(1) solid var(--global--border--Color);
  }

  &__widget-selector {
    background: var(--global--white--Color);
    box-shadow: 0 size(1) size(3) rgba(0, 0, 0, 0.1),
      0 size(2) size(1) rgba(0, 0, 0, 0.06),
      0 size(1) size(1) rgba(0, 0, 0, 0.08);
    border-radius: size(8);
    padding: size(20);
    margin-bottom: size(20);

    h4 {
      @include font-styles($type: 'text', $size: 'lg', $weight: 'bold');
      color: var(--global--main--title--Color);
      margin-bottom: size(16);
    }
  }

  &__widget-tabs {
    display: flex;
    gap: size(2);
    border-bottom: size(2) solid var(--global--border--Color);
    margin-bottom: size(14);
  }

  &__widget-tab {
    @include font-styles($type: 'label', $size: 'lg', $weight: 'bold');
    padding: size(10) size(20);
    background: none;
    border: none;
    cursor: pointer;
    color: var(--global--main--Color);
    border-bottom: size(2) solid transparent;
    margin-bottom: size(-2);
    transition: color 0.15s, border-color 0.15s;

    &:hover {
      color: var(--global--common-link--Color);
    }

    &--active {
      color: var(--global--common-link--Color);
      border-bottom-color: var(--global--common-link--Color);
    }
  }

  &__widget-desc {
    @include font-styles($type: 'label', $size: 'lg', $weight: 'regular');
    color: var(--global--main--Color);
    margin-bottom: size(16);
  }

  &__code-preview {
    display: flex;
    gap: size(20);
    margin-bottom: size(20);
  }

  &__code-side {
    flex: 1;
    min-width: 0;
  }

  &__preview-side {
    width: size(360);
    flex-shrink: 0;
  }

  &__preview-label {
    @include font-styles($type: 'label', $size: 'lg', $weight: 'bold');
    color: var(--global--main--title--Color);
    padding: size(10) 0;
    margin-bottom: size(2);
  }

  &__preview-frame {
    background: var(--global--primary--BackgroundColor);
    border: size(1) solid var(--global--border--Color);
    border-radius: size(8);
    overflow: hidden;
    height: size(450);

    iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
  }

  &__tabs {
    display: flex;
    gap: size(2);
    border-bottom: size(2) solid var(--global--border--Color);
    margin-bottom: 0;
  }

  &__tab {
    @include font-styles($type: 'label', $size: 'lg', $weight: 'bold');
    padding: size(10) size(18);
    background: none;
    border: none;
    cursor: pointer;
    color: var(--global--main--Color);
    border-bottom: size(2) solid transparent;
    margin-bottom: size(-2);
    transition: color 0.15s, border-color 0.15s;

    &:hover {
      color: var(--global--common-link--Color);
    }

    &--active {
      color: var(--global--common-link--Color);
      border-bottom-color: var(--global--common-link--Color);
    }
  }

  &__code-container {
    background: var(--global--primary--BackgroundColor);
    border: size(1) solid var(--global--border--Color);
    border-top: none;
    border-radius: 0 0 size(8) size(8);
    overflow: hidden;
  }

  &__code-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: size(8) size(16);
    background: var(--global--line--table--BackgroundColor);
    border-bottom: size(1) solid var(--global--border--Color);
  }

  &__code-filename {
    font-family: Menlo, Consolas, monospace;
    font-size: size(12);
    color: var(--global--heading--Color);
    font-weight: 600;
  }

  &__copy-btn {
    font-family: inherit;
    @include font-styles($type: 'label', $size: 'sm', $weight: 'bold');
    padding: size(4) size(12);
    background: var(--global--white--Color);
    border: size(1) solid var(--global--border--Color);
    border-radius: size(4);
    color: var(--global--common-link--Color);
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: var(--global--line--table--BackgroundColor);
    }
  }

  &__code {
    padding: size(16) size(20);
    margin: 0;
    overflow-x: auto;
    max-height: size(400);
    overflow-y: auto;

    code {
      font-family: Menlo, Consolas, monospace;
      font-size: size(13);
      line-height: 1.6;
      color: var(--global--main--title--Color);
      white-space: pre;
    }
  }

  &__reference {
    background: var(--global--white--Color);
    box-shadow: 0 size(1) size(3) rgba(0, 0, 0, 0.1),
      0 size(2) size(1) rgba(0, 0, 0, 0.06),
      0 size(1) size(1) rgba(0, 0, 0, 0.08);
    border-radius: size(8);
    padding: size(20);
    margin-bottom: size(20);

    h4 {
      @include font-styles($type: 'text', $size: 'lg', $weight: 'bold');
      color: var(--global--main--title--Color);
      margin-bottom: size(20);
      margin-top: size(16);

      &:first-child {
        margin-top: 0;
      }
    }
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: size(10);

    th,
    td {
      @include font-styles($type: 'label', $size: 'lg', $weight: 'regular');
      text-align: left;
      padding: size(8) size(12);
      border-bottom: size(1) solid
        var(--global--line--table--BackgroundColor);
    }

    th {
      @include font-styles($type: 'label', $size: 'lg', $weight: 'bold');
      color: var(--global--main--title--Color);
      background: var(--global--dashboard-bg--Color);
    }

    td code {
      font-family: Menlo, Consolas, monospace;
      font-size: size(12);
      background: var(--global--primary--BackgroundColor);
      padding: size(1) size(4);
      border-radius: size(3);
      color: var(--global--common--Color);
    }
  }

  &__example {
    background: var(--global--white--Color);
    box-shadow: 0 size(1) size(3) rgba(0, 0, 0, 0.1),
      0 size(2) size(1) rgba(0, 0, 0, 0.06),
      0 size(1) size(1) rgba(0, 0, 0, 0.08);
    border-radius: size(8);
    padding: size(20);
    margin-bottom: size(20);

    h4 {
      @include font-styles($type: 'text', $size: 'lg', $weight: 'bold');
      color: var(--global--main--title--Color);
      margin-bottom: size(12);
    }
  }

  &__links {
    display: flex;
    gap: size(10);
  }
}
</style>
